import { formatDate } from '@/lib/format'
import type { Labeled } from '@/lib/tone'

export const AI_CAPABILITIES = ['text', 'vision', 'transcription', 'speech', 'image', 'web_search'] as const
export type AiCapability = (typeof AI_CAPABILITIES)[number]

export const AI_TASKS = [
  'attendant',
  'lead_qualifier',
  'assistant',
  'transcription',
  'speech',
  'price_suggestion',
  'price_search',
  'blog',
  'seo',
  'sales',
  'image_generation'
] as const
export type AiTask = (typeof AI_TASKS)[number]

export type AiProvider = 'anthropic' | 'openai-compatible'

export type AiRouteStatus =
  | 'ready'
  | 'disabled'
  | 'credential_missing'
  | 'credential_disabled'
  | 'no_credit'
  | 'limit_reached'
  | 'key_unavailable'
  | 'model_missing'
  | 'missing_capability'

export type AiCreditState = 'available' | 'disabled' | 'no_credit' | 'limit_reached'

export type AiFailureKind = 'no_credit' | 'rate_limited' | 'unavailable' | 'rejected'

export const AI_ROUTE_REASONING = ['none', 'low', 'medium', 'high'] as const
export type AiRouteReasoning = (typeof AI_ROUTE_REASONING)[number]

export interface AiCredentialModel {
  id: string
  capabilities: AiCapability[]
  inputUsdPerMTok?: number | null
  cachedInputUsdPerMTok?: number | null
  outputUsdPerMTok?: number | null
}

export interface AiCredential {
  id: string
  provider: AiProvider
  name: string
  baseUrl: string | null
  enabled: boolean
  keyPreview: string
  models: AiCredentialModel[]
  monthlyLimitUsd: number | null
  renewalDay: number
  credit: { state: AiCreditState; noCreditUntil: string | null; reason: string | null }
  cycle: { startedAt: string; endsAt: string; calls: number; inputTokens: number; cachedInputTokens: number; outputTokens: number; costUsd: number }
  readOnly: boolean
  createdAt: string
  updatedAt: string
}

export interface AiTaskRoute {
  id: string
  credentialId: string
  model: string
  enabled: boolean
  reasoning: AiRouteReasoning | null
  priority: number
  status: AiRouteStatus
}

export interface AiTaskView {
  task: AiTask
  capabilities: AiCapability[]
  routes: AiTaskRoute[]
}

export interface AiProviderCatalogEntry {
  provider: AiProvider
  capabilities: AiCapability[]
  defaultBaseUrl: string
  suggestedModels: AiCredentialModel[]
}

export interface AiRouting {
  source: 'database' | 'environment'
  encryptionConfigured: boolean
  tasks: AiTaskView[]
  credentials: AiCredential[]
  providers: AiProviderCatalogEntry[]
}

export interface AiCredentialTestResult {
  ok: boolean
  model: string
  durationMs: number
  failure: AiFailureKind | null
  status: number | null
  noCreditUntil: string | null
}

export type AiRouteDraft = Pick<AiTaskRoute, 'credentialId' | 'model' | 'enabled'> & { reasoning?: AiRouteReasoning | null }

export const AI_TASK_LABELS: Record<AiTask, { label: string; description: string }> = {
  attendant: { label: 'Atendente IA', description: 'Responde os clientes no WhatsApp, no site e no Instagram (e o teste da tela)' },
  lead_qualifier: { label: 'Qualificação de lead', description: 'Avalia a temperatura e o interesse do cliente depois de cada resposta' },
  assistant: { label: 'Assistente do painel', description: 'Conversa com a equipe no painel e lê as imagens enviadas' },
  transcription: { label: 'Transcrição de áudio', description: 'Áudio do cliente e voz do assistente viram texto' },
  speech: { label: 'Voz do assistente', description: 'Lê a resposta do assistente em voz alta' },
  price_suggestion: { label: 'Sugestão de preço', description: 'Parecer e estimativa pelo conhecimento do mercado' },
  price_search: { label: 'Busca de anúncios', description: 'Procura anúncios semelhantes na web para a sugestão de preço' },
  blog: { label: 'Blog', description: 'Tema e texto dos posts' },
  seo: { label: 'SEO do imóvel', description: 'Título e descrição para buscadores' },
  sales: { label: 'Vendas da Habitar', description: 'Avaliação dos leads e relatórios das pesquisas' },
  image_generation: { label: 'Geração de imagem', description: 'Cria imagens; só aceita modelos que geram imagem' }
}

export const AI_CAPABILITY_LABELS: Record<AiCapability, string> = {
  text: 'Texto',
  vision: 'Lê imagens',
  transcription: 'Transcrição',
  speech: 'Voz',
  image: 'Gera imagem',
  web_search: 'Busca na web'
}

export const AI_PROVIDER_LABELS: Record<AiProvider, string> = {
  anthropic: 'Anthropic (Claude)',
  'openai-compatible': 'Compatível com OpenAI'
}

export const AI_ROUTE_STATUS: Record<AiRouteStatus, Labeled> = {
  ready: { label: 'Pronto', tone: 'success' },
  disabled: { label: 'Desligado', tone: 'neutral' },
  credential_missing: { label: 'Credencial removida', tone: 'danger' },
  credential_disabled: { label: 'Credencial desligada', tone: 'neutral' },
  no_credit: { label: 'Sem crédito', tone: 'warning' },
  limit_reached: { label: 'Limite do mês', tone: 'warning' },
  key_unavailable: { label: 'Chave indisponível', tone: 'danger' },
  model_missing: { label: 'Modelo removido', tone: 'danger' },
  missing_capability: { label: 'Não serve para a tarefa', tone: 'danger' }
}

export const AI_CREDIT_STATE: Record<AiCreditState, Labeled> = {
  available: { label: 'Com crédito', tone: 'success' },
  disabled: { label: 'Desligada', tone: 'neutral' },
  no_credit: { label: 'Sem crédito', tone: 'warning' },
  limit_reached: { label: 'Limite do mês', tone: 'warning' }
}

export const AI_ROUTING_ERROR_MESSAGES: Record<string, string> = {
  AI_CREDENTIALS_KEY_MISSING: 'Defina a variável AI_CREDENTIALS_KEY no ai-service para gravar chaves.',
  AI_MODEL_CAPABILITY_MISSING: 'Esse modelo não faz o que a tarefa precisa.',
  AI_MODEL_CAPABILITY_UNSUPPORTED: 'Esse provedor não oferece essa capacidade.',
  AI_PROVIDER_NOT_CONFIGURED: 'Nenhum modelo disponível para esta tarefa agora.'
}

export const AI_ROUTE_LIMIT = 10

const usd = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
const compact = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 })

export const formatUsd = (value: number): string => usd.format(value)

export const formatTokens = (value: number): string => `${compact.format(value)} ${value === 1 ? 'token' : 'tokens'}`

export const routeStatus = (status: AiRouteStatus): Labeled => AI_ROUTE_STATUS[status] ?? { label: status, tone: 'neutral' }

export const creditState = (credential: Pick<AiCredential, 'credit'>): Labeled =>
  AI_CREDIT_STATE[credential.credit.state] ?? AI_CREDIT_STATE.available

export function creditDetail(credential: Pick<AiCredential, 'credit' | 'renewalDay'>): string {
  if (credential.credit.state === 'no_credit' && credential.credit.noCreditUntil)
    return `Fora da fila até ${formatDate(credential.credit.noCreditUntil)}`
  if (credential.credit.state === 'limit_reached') return 'Volta na renovação'
  return `Renova todo dia ${credential.renewalDay}`
}

export function cycleSummary(credential: Pick<AiCredential, 'cycle' | 'monthlyLimitUsd'>): string {
  const { cycle, monthlyLimitUsd } = credential
  const cost = monthlyLimitUsd !== null ? `${formatUsd(cycle.costUsd)} de ${formatUsd(monthlyLimitUsd)}` : formatUsd(cycle.costUsd)
  return `${cost} · ${formatTokens(cycle.inputTokens + cycle.outputTokens)} · ${cycle.calls.toLocaleString('pt-BR')} ${cycle.calls === 1 ? 'chamada' : 'chamadas'}`
}

export const cyclePercent = (credential: Pick<AiCredential, 'cycle' | 'monthlyLimitUsd'>): number | null =>
  credential.monthlyLimitUsd ? Math.min(100, Math.round((credential.cycle.costUsd / credential.monthlyLimitUsd) * 100)) : null

export const servesTask = (model: Pick<AiCredentialModel, 'capabilities'>, required: readonly AiCapability[]): boolean =>
  required.every(capability => model.capabilities.includes(capability))

export const routeKey = (route: Pick<AiRouteDraft, 'credentialId' | 'model'>): string => `${route.credentialId}/${route.model}`

export function moveRoute<T>(routes: readonly T[], index: number, delta: number): T[] {
  const target = index + delta
  if (index < 0 || index >= routes.length || target < 0 || target >= routes.length) return [...routes]
  const next = [...routes]
  const [item] = next.splice(index, 1)
  next.splice(target, 0, item)
  return next
}

export const toggleRoute = <T extends AiRouteDraft>(routes: readonly T[], index: number, enabled: boolean): T[] =>
  routes.map((route, position) => (position === index ? { ...route, enabled } : route))

export const AI_REASONING_LABELS: Record<AiRouteReasoning, string> = { none: 'Desligado', low: 'Baixo', medium: 'Médio', high: 'Alto' }

export const reasoningLabel = (reasoning: AiRouteReasoning | null | undefined, provider: AiProvider | null): string =>
  reasoning ? AI_REASONING_LABELS[reasoning] : provider === 'anthropic' ? 'Padrão (baixo)' : 'Padrão'

export const setRouteReasoning = <T extends AiRouteDraft>(routes: readonly T[], index: number, reasoning: AiRouteReasoning | null): T[] =>
  routes.map((route, position) => (position === index ? { ...route, reasoning } : route))

export const removeRoute = <T>(routes: readonly T[], index: number): T[] => routes.filter((_, position) => position !== index)

export const routesPayload = (routes: readonly AiRouteDraft[]): { routes: AiRouteDraft[] } => ({
  routes: routes.map(({ credentialId, model, enabled, reasoning }) => ({ credentialId, model, enabled, reasoning: reasoning ?? null }))
})

export interface AddableModel {
  credentialId: string
  credentialName: string
  model: string
}

export function addableModels(
  task: { capabilities: readonly AiCapability[]; routes: readonly AiRouteDraft[] },
  credentials: readonly AiCredential[]
): AddableModel[] {
  const used = new Set(task.routes.map(routeKey))
  return credentials
    .filter(credential => !credential.readOnly)
    .flatMap(credential =>
      credential.models
        .filter(model => servesTask(model, task.capabilities) && !used.has(routeKey({ credentialId: credential.id, model: model.id })))
        .map(model => ({ credentialId: credential.id, credentialName: credential.name, model: model.id }))
    )
}

export const credentialName = (credentials: readonly Pick<AiCredential, 'id' | 'name'>[], id: string): string =>
  credentials.find(credential => credential.id === id)?.name ?? 'Credencial removida'

export function testResultText(result: AiCredentialTestResult): string {
  if (result.ok) return `${result.model} respondeu em ${(result.durationMs / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s.`
  if (result.failure === 'no_credit')
    return result.noCreditUntil ? `Sem crédito. Fica fora da fila até ${formatDate(result.noCreditUntil)}.` : 'Sem crédito no provedor.'
  if (result.failure === 'rate_limited') return 'O provedor está limitando as chamadas agora. Tente de novo em instantes.'
  if (result.failure === 'rejected') return 'O provedor recusou a chave ou o modelo.'
  return 'O provedor não respondeu.'
}

export interface CredentialModelForm {
  id: string
  capabilities: AiCapability[]
  inputUsdPerMTok: string
  cachedInputUsdPerMTok: string
  outputUsdPerMTok: string
}

export interface CredentialForm {
  provider: AiProvider
  name: string
  baseUrl: string
  apiKey: string
  enabled: boolean
  models: CredentialModelForm[]
  monthlyLimitUsd: string
  renewalDay: string
  prioritize: string
}

export type CredentialFormErrors = Partial<Record<'name' | 'baseUrl' | 'apiKey' | 'models' | 'monthlyLimitUsd' | 'renewalDay', string>>

const priceText = (value: number | null | undefined): string => (value === null || value === undefined ? '' : String(value).replace('.', ','))

export const modelFormOf = (model: AiCredentialModel): CredentialModelForm => ({
  id: model.id,
  capabilities: [...model.capabilities],
  inputUsdPerMTok: priceText(model.inputUsdPerMTok),
  cachedInputUsdPerMTok: priceText(model.cachedInputUsdPerMTok),
  outputUsdPerMTok: priceText(model.outputUsdPerMTok)
})

export const emptyModelForm = (provider: AiProvider): CredentialModelForm => ({
  id: '',
  capabilities: provider === 'anthropic' ? ['text', 'vision'] : ['text'],
  inputUsdPerMTok: '',
  cachedInputUsdPerMTok: '',
  outputUsdPerMTok: ''
})

export const providerCapabilities = (providers: readonly AiProviderCatalogEntry[], provider: AiProvider): AiCapability[] =>
  providers.find(entry => entry.provider === provider)?.capabilities ?? (provider === 'anthropic' ? ['text', 'vision'] : [...AI_CAPABILITIES])

export function emptyCredentialForm(provider: AiProvider, providers: readonly AiProviderCatalogEntry[]): CredentialForm {
  const suggested = providers.find(entry => entry.provider === provider)?.suggestedModels ?? []
  const models = suggested.length > 0 ? suggested.map(modelFormOf) : [emptyModelForm(provider)]
  return {
    provider,
    name: provider === 'anthropic' ? 'Claude' : '',
    baseUrl: '',
    apiKey: '',
    enabled: true,
    models,
    monthlyLimitUsd: '',
    renewalDay: '1',
    prioritize: provider === 'anthropic' ? (suggested.find(model => model.capabilities.includes('text'))?.id ?? '') : ''
  }
}

export const credentialFormOf = (credential: AiCredential): CredentialForm => ({
  provider: credential.provider,
  name: credential.name,
  baseUrl: credential.baseUrl ?? '',
  apiKey: '',
  enabled: credential.enabled,
  models: credential.models.map(modelFormOf),
  monthlyLimitUsd: credential.monthlyLimitUsd === null ? '' : String(credential.monthlyLimitUsd).replace('.', ','),
  renewalDay: String(credential.renewalDay),
  prioritize: ''
})

const parseDecimal = (value: string): number | null => {
  const normalized = value.trim().replace(/\./g, '').replace(',', '.')
  if (!normalized) return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : Number.NaN
}

const MODEL_ID = /^[\w.:/@-]+$/

export function validateCredentialForm(form: CredentialForm, editing: boolean): CredentialFormErrors {
  const errors: CredentialFormErrors = {}
  if (!form.name.trim()) errors.name = 'Dê um nome para reconhecer a credencial.'
  if (form.baseUrl.trim() && !/^https?:\/\/[^\s]+$/i.test(form.baseUrl.trim())) errors.baseUrl = 'Use uma URL completa, começando com https://.'
  if (!editing && form.apiKey.trim().length < 8) errors.apiKey = 'Cole a chave da API.'
  if (editing && form.apiKey.trim() && form.apiKey.trim().length < 8) errors.apiKey = 'A chave parece incompleta.'
  const ids = form.models.map(model => model.id.trim())
  if (form.models.length === 0) errors.models = 'Adicione pelo menos um modelo.'
  else if (ids.some(id => !id)) errors.models = 'Preencha o nome de cada modelo.'
  else if (ids.some(id => !MODEL_ID.test(id))) errors.models = 'O nome do modelo não pode ter espaços.'
  else if (new Set(ids).size !== ids.length) errors.models = 'Há modelos repetidos.'
  else if (form.models.some(model => model.capabilities.length === 0)) errors.models = 'Marque o que cada modelo faz.'
  else if (
    form.models.some(model =>
      [model.inputUsdPerMTok, model.cachedInputUsdPerMTok, model.outputUsdPerMTok].some(
        value => Number.isNaN(parseDecimal(value) ?? 0) || (parseDecimal(value) ?? 0) < 0
      )
    )
  )
    errors.models = 'Preço inválido.'
  const limit = parseDecimal(form.monthlyLimitUsd)
  if (limit !== null && (Number.isNaN(limit) || limit <= 0)) errors.monthlyLimitUsd = 'Use um valor maior que zero.'
  const day = Number(form.renewalDay)
  if (!Number.isInteger(day) || day < 1 || day > 28) errors.renewalDay = 'Escolha um dia de 1 a 28.'
  return errors
}

const modelPayload = (model: CredentialModelForm): AiCredentialModel => ({
  id: model.id.trim(),
  capabilities: model.capabilities,
  inputUsdPerMTok: parseDecimal(model.inputUsdPerMTok),
  cachedInputUsdPerMTok: parseDecimal(model.cachedInputUsdPerMTok),
  outputUsdPerMTok: parseDecimal(model.outputUsdPerMTok)
})

export interface CredentialInput {
  provider?: AiProvider
  name?: string
  baseUrl?: string | null
  apiKey?: string
  enabled?: boolean
  models?: AiCredentialModel[]
  monthlyLimitUsd?: number | null
  renewalDay?: number
  prioritize?: { model: string }
}

export function credentialInputOf(form: CredentialForm, editing: AiCredential | null): CredentialInput {
  const input: CredentialInput = {
    name: form.name.trim(),
    baseUrl: form.baseUrl.trim() || null,
    enabled: form.enabled,
    models: form.models.map(modelPayload),
    monthlyLimitUsd: parseDecimal(form.monthlyLimitUsd),
    renewalDay: Number(form.renewalDay)
  }
  if (form.apiKey.trim()) input.apiKey = form.apiKey.trim()
  if (editing) return input
  return { ...input, provider: form.provider, ...(form.prioritize ? { prioritize: { model: form.prioritize } } : {}) }
}
